
import React from 'react';
import { useAuth } from '../context/AuthContext';
import { useAllOrders } from '../context/AllOrdersContext';
import { ChartBarIcon, DeliveryIcon } from '../components/icons';
import { parseDate, formatDateReadable } from '../utils/dateUtils';


const LivreurAccountingScreen: React.FC = () => {
    const { currentUser } = useAuth();
    const { placedOrders, loading } = useAllOrders();

    const deliveredOrders = placedOrders.filter(o =>
        o.assignedDriver?.id === currentUser?.uid &&
        (o.status === 'Livrée' || o.status === 'Terminée')
    );

    const now = new Date();

    // Time periods
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const weekStart = new Date(now);
    const day = weekStart.getDay();
    const diff = weekStart.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
    weekStart.setDate(diff);
    weekStart.setHours(0, 0, 0, 0);

    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    // Sums
    const calculateEarnings = (startDate: Date) => {
        return deliveredOrders
            .filter(o => {
                const date = parseDate(o.timestamp);
                return date >= startDate;
            })
            .reduce((sum, o) => sum + (o.deliveryFee || 0), 0);
    };

    const earningsToday = calculateEarnings(todayStart);
    const earningsWeek = calculateEarnings(weekStart);
    const earningsMonth = calculateEarnings(monthStart);

    return (
        <div className="min-h-screen bg-white pb-24">
            {/* Massive Editorial Header */}
            <div className="bg-black text-white px-8 pt-12 pb-16 rounded-b-[3.5rem] shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-red-600/10 rounded-full blur-3xl -mr-32 -mt-32 animate-pulse" />
                <div className="absolute left-0 bottom-0 w-48 h-48 bg-blue-600/5 rounded-full blur-3xl -ml-24 -mb-24" />
                
                <div className="flex justify-between items-center mb-10 relative z-10">
                    <h1 className="text-sm font-black text-gray-400 tracking-[0.3em] uppercase italic">
                        Hub <span className="text-white">Comptabilité</span>
                    </h1>
                    <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center backdrop-blur-md border border-white/10 shadow-xl">
                        <ChartBarIcon className="w-6 h-6 text-white" />
                    </div>
                </div>

                <div className="text-center relative z-10">
                    <p className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em] mb-4 italic">Bilan Mensuel</p>
                    <div className="flex flex-col items-center">
                        <p className="text-7xl font-black italic tracking-tighter leading-none mb-2">
                            {earningsMonth}
                        </p>
                        <span className="text-xs font-black uppercase tracking-[0.4em] text-red-600 italic">Dinars Algériens</span>
                    </div>
                </div>
            </div>

            <div className="px-6 -mt-10 space-y-8 relative z-20">
                {/* Period Highlights - Premium Pill Cards */}
                <div className="grid grid-cols-2 gap-5">
                    <div className="bg-white p-7 rounded-[2.5rem] shadow-[0_20px_40px_-15px_rgba(0,0,0,0.1)] border border-gray-100 flex flex-col justify-between h-40 group hover:shadow-2xl transition-all active:scale-[0.98]">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="w-2 h-2 rounded-full bg-red-600 animate-pulse" />
                            <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 italic">Aujourd'hui</p>
                        </div>
                        <div>
                            <p className="text-3xl font-black text-gray-900 italic tracking-tighter mb-1">{earningsToday}</p>
                            <div className="w-12 h-1 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full bg-red-600 w-2/3 rounded-full" />
                            </div>
                        </div>
                    </div>
                    
                    <div className="bg-white p-7 rounded-[2.5rem] shadow-[0_20px_40px_-15px_rgba(0,0,0,0.1)] border border-gray-100 flex flex-col justify-between h-40 hover:shadow-2xl transition-all active:scale-[0.98]">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="w-2 h-2 rounded-full bg-black shadow-lg shadow-gray-400" />
                            <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 italic">Semaine</p>
                        </div>
                        <div>
                            <p className="text-3xl font-black text-gray-900 italic tracking-tighter mb-1">{earningsWeek}</p>
                            <div className="w-12 h-1 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full bg-black w-1/2 rounded-full" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Growth Section Indicator */}
                <div className="bg-gray-50/50 p-6 rounded-[2rem] border border-gray-100 flex items-center justify-between group">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-black text-white rounded-2xl flex items-center justify-center text-xl shadow-xl">📈</div>
                        <div>
                            <p className="text-[10px] font-black text-gray-900 uppercase tracking-widest italic leading-none mb-1">Performance Hub</p>
                            <p className="text-[9px] text-gray-400 font-bold uppercase tracking-tighter">Croissance de +12% ce mois-ci</p>
                        </div>
                    </div>
                    <span className="text-red-600 font-black italic text-sm">PRO</span>
                </div>

                {/* Earnings List - Editorial Grid */}
                <section className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden pb-4">
                    <div className="p-7 border-b border-gray-50 flex justify-between items-baseline">
                        <h2 className="text-sm font-black text-gray-900 tracking-[0.2em] uppercase italic leading-none">Historique Recettes</h2>
                        <span className="text-[9px] font-black uppercase text-gray-300 italic tracking-tighter">{deliveredOrders.length} COURSES</span>
                    </div>

                    {loading ? (
                        <div className="p-16 flex flex-col items-center gap-4">
                            <div className="w-10 h-10 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                            <p className="text-[9px] font-black text-gray-400 uppercase italic tracking-widest">Loading Records...</p>
                        </div>
                    ) : deliveredOrders.length > 0 ? (
                        <div className="divide-y divide-gray-50/50 px-2">
                            {deliveredOrders.slice(0, 15).map(order => (
                                <div key={order.id} className="p-5 flex justify-between items-center hover:bg-gray-50/50 transition-all rounded-[1.5rem] mx-2">
                                    <div className="flex items-center gap-5">
                                        <div className="w-14 h-14 bg-gray-50 text-gray-900 rounded-[1.2rem] flex items-center justify-center shadow-inner border border-gray-100">
                                            <DeliveryIcon className="w-6 h-6" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-black text-gray-900 uppercase tracking-tighter italic leading-tight mb-1">{order.items[0]?.restaurantName}</p>
                                            <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest italic opacity-70">
                                                {formatDateReadable(order.timestamp)}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-lg font-black text-black italic tracking-tighter">+{order.deliveryFee} <span className="text-[10px]">DA</span></p>
                                        <span className="text-[8px] font-black bg-green-50 text-green-600 px-2 py-0.5 rounded-full uppercase italic tracking-tighter border border-green-100">✓ Soldé</span>
                                    </div>
                                </div>
                            ))}
                            {deliveredOrders.length > 15 && (
                                <div className="p-8 text-center">
                                    <p className="text-[9px] font-black text-gray-300 uppercase italic tracking-[0.3em]">Affichage des 15 derniers relevés</p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="p-20 text-center flex flex-col items-center gap-4">
                            <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center grayscale opacity-20">💰</div>
                            <p className="text-[10px] font-black text-gray-400 uppercase italic tracking-widest">Aucun gain enregistré</p>
                        </div>
                    )}
                </section>

                {/* Info Card - Editorial Style */}
                <div className="bg-black text-white p-6 rounded-[2.5rem] shadow-2xl relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -mr-12 -mt-12 group-hover:scale-110 transition-transform" />
                    <p className="text-[9px] text-gray-500 font-black uppercase tracking-[0.2em] italic mb-3">Note de Transparence</p>
                    <p className="text-xs text-gray-400 font-bold leading-relaxed italic">
                        Vos revenus sont calculés automatiquement en fonction de la wilaya et de la distance de chaque course validée par le client.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default LivreurAccountingScreen;
